import { Announcement, ContentLang } from './content/announcements';

// Pure logic for the "What's new" announcements dialog. No React, no storage:
// everything here is unit-testable.

// Returns the epoch millis of the last instant of the given ISO day ("YYYY-MM-DD")
// in local time, or null if the string is not a valid ISO date.
function endOfDayLocal(isoDate: string): number | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    if (!match) {
        return null;
    }
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 23, 59, 59, 999);
    return isNaN(date.getTime()) ? null : date.getTime();
}

export function sortAnnouncementsNewestFirst(list: Announcement[]): Announcement[] {
    return [...list].sort((a, b) => {
        if (a.date !== b.date) {
            return a.date < b.date ? 1 : -1;
        }
        return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
    });
}

// Keeps only announcements that have not expired at `now`. `expiresAt` is
// inclusive: the announcement is still shown during the whole expiry day.
export function filterActiveAnnouncements(all: Announcement[], now: Date): Announcement[] {
    return all.filter((announcement) => {
        if (!announcement.expiresAt) {
            return true;
        }
        const expiry = endOfDayLocal(announcement.expiresAt);
        if (expiry === null) {
            return false;
        }
        return now.getTime() <= expiry;
    });
}

export function getUnseenAnnouncements(all: Announcement[], seenIds: string[]): Announcement[] {
    const seen = new Set(seenIds);
    return sortAnnouncementsNewestFirst(all.filter((announcement) => !seen.has(announcement.id)));
}

export function langToContentKey(lang: string | undefined): ContentLang {
    return lang && lang.startsWith('fr') ? 'fr' : 'en';
}

export function parseSeenIds(raw: string | null): string[] {
    if (!raw) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter((id): id is string => typeof id === 'string');
    } catch {
        return [];
    }
}

// Drops seen ids whose announcement no longer exists or has expired: such an
// announcement can never be displayed again (filterActiveAnnouncements excludes
// it), so remembering it only makes the stored list grow forever. Requires ids
// to never be reused for a new announcement (the "YYYY-MM-DD-slug" convention).
export function pruneSeenIds(seenIds: string[], activeAnnouncements: Announcement[]): string[] {
    const activeIds = new Set(activeAnnouncements.map((announcement) => announcement.id));
    return seenIds.filter((id) => activeIds.has(id));
}

export function mergeSeenIds(existing: string[], newlySeen: string[]): string[] {
    const merged: string[] = [];
    const seen = new Set<string>();
    for (const id of [...existing, ...newlySeen]) {
        if (!seen.has(id)) {
            seen.add(id);
            merged.push(id);
        }
    }
    return merged;
}
